import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  OnInit,
  Signal,
  SimpleChanges,
  inject,
  input,
  signal,
} from '@angular/core';
import { TargetingService } from '@scripts/woo_services.module/TargetingService';
import { TranslationService } from '@scripts/woo_services.module/TranslationService';
import {
  Campaign,
  OmsBookableCheck,
  OmsBookableCheckRequestEntry,
  OmsBookableCheckWooFigures,
  wooId,
} from '@scripts/woo_services.module/shared-types';
import { ApiState } from '@src/services/models/api-state.type';
import { OMSBookableChecksAPIService } from '@src/services/oms-bookable-checks-api.service';
import { DotLoader } from '@src/shared/feedback/loader/dot-loader.component';
import { WooCodeComponent } from '@src/shared/presentational/woo-code/woo-code.component';
import { WooTableColumn } from '@src/shared/presentational/woo-table/woo-table-column/woo-table-column.component';
import { WooTableComponent } from '@src/shared/presentational/woo-table/woo-table.component';

const EVENT_TEXTS: Record<string, string> = {
  booked: 'Bokning',
  updated: 'Uppdatering',
};

const VERDICT_TEXTS: Record<string, string> = {
  bookable: 'OK',
  not_bookable: 'Ej bokningsbart',
  oms_unavailable: 'OMS otillgängligt',
};

const VERDICT_CLASSES: Record<string, string> = {
  bookable: 'text--success',
  not_bookable: 'text--danger',
  oms_unavailable: 'text--warning',
};

const WOO_VERDICT_TEXTS: Record<string, string> = {
  ok: 'OK',
  insufficient: 'Otillräckligt lager',
  sold_out: 'Slutsålt',
  error: 'Fel',
};

const WOO_VERDICT_CLASSES: Record<string, string> = {
  ok: 'text--success',
  insufficient: 'text--warning',
  sold_out: 'text--danger',
  error: 'text--danger',
};

@Component({
  selector: 'oms-bookable-checks',
  imports: [CommonModule, DotLoader, WooCodeComponent, WooTableColumn, WooTableComponent],
  templateUrl: './oms-bookable-checks.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OmsBookableChecksComponent implements OnChanges, OnInit {
  readonly campaign = input.required<Campaign>();

  private readonly api = inject(OMSBookableChecksAPIService);
  private readonly targetingService = inject(TargetingService);
  private readonly translationService = inject(TranslationService);
  private fetchedCampaignId: wooId | null = null;

  state: Signal<ApiState<OmsBookableCheck[]>> = signal<ApiState<OmsBookableCheck[]>>({ status: 'loading' });

  readonly wooFigureFields: { key: keyof OmsBookableCheckWooFigures; prefix: string; alwaysImpressions?: boolean }[] = [
    { key: 'requested_views', prefix: 'Begärda' },
    { key: 'bookable_views', prefix: 'Bokningsbara' },
    { key: 'overbooked_impressions', prefix: 'Överbokade' },
    { key: 'booked_views', prefix: 'Bokade' },
    { key: 'inventory_views', prefix: 'Lager', alwaysImpressions: true },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['campaign']) {
      this.fetchChecks();
    }
  }

  ngOnInit(): void {
    this.fetchChecks();
  }

  private fetchChecks(): void {
    const campaignId = this.campaign().id;
    if (campaignId === this.fetchedCampaignId) {
      return;
    }
    this.fetchedCampaignId = campaignId;
    this.state = this.api.getBookableChecks(campaignId);
  }

  eventText = (value: string): string => EVENT_TEXTS[value] ?? value;
  verdictText = (value: string): string => VERDICT_TEXTS[value] ?? value;
  verdictClass = (value: string): string => VERDICT_CLASSES[value] ?? '';
  wooVerdictText = (value: string | null): string => (value == null ? '–' : WOO_VERDICT_TEXTS[value] ?? value);
  wooVerdictClass = (value: string | null): string => (value == null ? '' : WOO_VERDICT_CLASSES[value] ?? '');
  strategyText = (value: string): string => (value === 'forced' ? 'Tvingad (överbokad)' : value);

  wooFigureLabel(field: { prefix: string; alwaysImpressions?: boolean }, woo: OmsBookableCheckWooFigures): string {
    const unit = field.alwaysImpressions
      ? 'impressions'
      : woo.view_currency == null
      ? ''
      : this.translationService.convertViewCurrency(woo.view_currency);
    return unit ? `${field.prefix} ${unit}` : field.prefix;
  }

  targetingLabel(entry: OmsBookableCheckRequestEntry): string {
    const targetings = this.campaign().targetings ?? [];
    const index = targetings.findIndex((targeting) => targeting.id === entry.targeting_id);
    const externalId = entry.targeting_external_id ? ` (${entry.targeting_external_id})` : '';
    return index >= 0 ? `Styrning ${index + 1}${externalId}` : entry.targeting_external_id ?? entry.targeting_id;
  }

  targetingInfo(entry: OmsBookableCheckRequestEntry): string | null {
    const targeting = (this.campaign().targetings ?? []).find((t) => t.id === entry.targeting_id);
    if (!targeting) {
      return null;
    }
    const type = this.targetingService.getTargetingName(targeting);
    const names = [
      targeting.regions,
      targeting.program_formats,
      this.targetingService.getTargetGroupNames(targeting).map((name) => ({ name })),
      targeting.advanced_target_groups,
      targeting.categories,
      targeting.device_groups,
      targeting.behaviors,
    ]
      .flat()
      .map((item) => item?.name)
      .filter(Boolean)
      .join(', ');
    if (!type) {
      return names || null;
    }
    return names ? `${type} (${names})` : type;
  }
}
